// Activity streak calculation.
//
// A streak is a run of consecutive local-calendar days on which the user
// logged at least one activity. `currentStreak` is the streak ending today
// (or ending yesterday if nothing is logged today yet — we do not break the
// streak until a full day has passed without activity). `longestStreak` is
// the longest such run anywhere in the user's history.
//
// Day boundaries are computed in the client's local timezone to match
// dateRanges.js — using new Date(year, month, date) so DST transitions are
// handled correctly by the runtime.

import { toMillis } from './timestamps';
import { getDateKey } from './dateDivider';

/**
 * Computes current and longest activity streaks from an activities array.
 *
 * Decisions:
 * - Today counts toward the current streak if any activity was logged today.
 *   If nothing has been logged today yet, we still consider yesterday as the
 *   anchor so a user who hasn't worked out yet today doesn't see their streak
 *   reset to 0 before the day is over.
 * - Empty or malformed timestamps (toMillis === 0) are ignored.
 * - Client-side only: no Firestore reads.
 *
 * @param {Array<{timestamp: any}>} activities
 * @returns {{ currentStreak: number, longestStreak: number }}
 */
export function calculateStreak(activities) {
  if (!Array.isArray(activities) || activities.length === 0) {
    return { currentStreak: 0, longestStreak: 0 };
  }

  // Build a set of local-day keys that have at least one activity.
  const dayKeys = new Set();
  for (const act of activities) {
    const ms = toMillis(act.timestamp);
    if (!ms) continue;
    dayKeys.add(getDateKey(ms));
  }
  if (dayKeys.size === 0) {
    return { currentStreak: 0, longestStreak: 0 };
  }

  // --- Current streak: walk backwards from today ---
  const now = new Date();
  const todayAnchor = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  let currentStreak = 0;

  // If today has no activity, start counting from yesterday so a partially
  // completed today doesn't drop the user's visible streak.
  let cursor = new Date(todayAnchor);
  if (!dayKeys.has(getDateKey(cursor.getTime()))) {
    cursor.setDate(cursor.getDate() - 1);
  }
  while (dayKeys.has(getDateKey(cursor.getTime()))) {
    currentStreak += 1;
    cursor.setDate(cursor.getDate() - 1);
  }

  // --- Longest streak: scan sorted unique day keys ---
  // Convert each key back to a day-anchor timestamp so we can compare adjacency
  // in calendar days. Using UTC math on the reconstructed Date is safe because
  // each anchor is midnight local; differences in whole days remain whole days
  // except across DST, which is why we re-derive the "next day" via local-time
  // Date mutation below instead of subtracting ms.
  const dayAnchors = Array.from(dayKeys)
    .map((k) => {
      const [y, m, d] = k.split('-').map(Number);
      return new Date(y, m, d).getTime();
    })
    .sort((a, b) => a - b);

  let longestStreak = 1;
  let run = 1;
  for (let i = 1; i < dayAnchors.length; i++) {
    const prev = new Date(dayAnchors[i - 1]);
    prev.setDate(prev.getDate() + 1);
    if (prev.getTime() === dayAnchors[i]) {
      run += 1;
      if (run > longestStreak) longestStreak = run;
    } else {
      run = 1;
    }
  }

  // currentStreak can exceed the scanned longest in edge cases (e.g. single
  // day). Ensure longest is at least currentStreak.
  if (currentStreak > longestStreak) longestStreak = currentStreak;

  return { currentStreak, longestStreak };
}
